import { Box, ListItem, SvgIcon, Theme, Tooltip, Typography } from '@mui/material';
import { ElementType, FC } from 'react';
import { NavigateFunction, useNavigate } from 'react-router';
import { MenuConfig, MenuElement } from '.';
import { NO_OP } from '../../../../../../misc/noOp';
import { SubmenuIndicator } from './toggleSubmenuIcon';

const PADDING_LEFT = 56;

type Props = {
  title: string;
  link?: string;
  onClick?: () => void;
  icon?: ElementType;
  secondaryIcon?: ElementType;
  isActive: boolean;
  disabled?: boolean;
  isBaseLevel?: boolean;
  childrenConfig?: MenuConfig;
  tooltip?: string;
  hasTodo?: boolean;
};

export const PageMenuItem: FC<Props> = ({
  title,
  link,
  onClick,
  isActive,
  disabled,
  icon,
  secondaryIcon,
  childrenConfig,
  isBaseLevel = false,
  tooltip = '',
  hasTodo = false
}) => {
  const navigate = useNavigate();
  const hasSubmenu = !!childrenConfig && childrenConfig.length > 0;
  const isActiveWithSubmenu = isActive && hasSubmenu;
  const subMenuIsOpen = hasSubmenu && isActive;
  // An open submenu shows the marked child itself, so rolling the dot up to
  // the parent as well would state it twice, diagonally above its own entry.
  const showsTodo = hasTodo || (!subMenuIsOpen && !!childrenConfig?.some((child) => child.hasTodo));
  const paddingLeft = isBaseLevel ? '0px' : `${PADDING_LEFT}px`;

  const onClickHandler = hasSubmenu
    ? getOnClickHandler(childrenConfig[0], false, navigate)
    : getOnClickHandler({ link, onClick, disabled }, isActiveWithSubmenu, navigate);

  return (
    <ListItem
      sx={{
        paddingLeft,
        paddingY: '0px',
        marginBottom: getMargin(isBaseLevel, subMenuIsOpen),
        display: 'block',
        width: '100%'
      }}
      onClick={onClickHandler}
    >
      <Box
        display="flex"
        alignItems="center"
        sx={(theme) => ({
          cursor: isActiveWithSubmenu || disabled ? 'unset' : 'pointer',
          '&:hover p, &:hover svg': {
            color: !isActive && !disabled ? theme.palette.grey['500'] : undefined
          },
          'p, svg': {
            color: disabled
              ? theme.palette.grey['100']
              : getMenuTextColor(isActive, isBaseLevel && hasSubmenu, theme)
          }
        })}
      >
        {icon && <SvgIcon component={icon} sx={{ width: `${PADDING_LEFT}px` }} />}
        <Tooltip title={tooltip} placement="right" arrow>
          <Typography
            sx={(theme) => ({
              fontWeight: isActive ? 'bold' : 'normal',
              fontSize: '1rem',
              flexGrow: tooltip ? 0 : 1,
              color: getMenuTextColor(isActive, isBaseLevel, theme)
            })}
          >
            {title}
          </Typography>
        </Tooltip>
        {showsTodo && (
          <Box
            component="span"
            aria-hidden
            className="ml-2 h-2 w-2 shrink-0 rounded-full bg-error-main"
          />
        )}
        {secondaryIcon && <SvgIcon component={secondaryIcon} sx={{ height: '17.5px' }} />}
        {hasSubmenu && <SubmenuIndicator open={isActiveWithSubmenu} />}
      </Box>
    </ListItem>
  );
};

const getOnClickHandler = (
  { link, onClick, disabled }: Pick<MenuElement, 'onClick' | 'link' | 'disabled'>,
  isActiveWithSubmenu: boolean,
  navigate: NavigateFunction
): (() => void) => {
  if (disabled || isActiveWithSubmenu) {
    return NO_OP;
  }

  if (link) {
    return () => navigate(link);
  }

  if (onClick) {
    return onClick;
  }

  return NO_OP;
};

const getMenuTextColor = (isActive: boolean, isBaseLevel: boolean, theme: Theme) => {
  if (isBaseLevel && isActive) {
    return theme.palette.primary.main;
  }
  if (isActive) {
    return theme.palette.heaven.main;
  }
  return undefined;
};

const getMargin = (isBaseLevel: boolean, subMenuIsOpen: boolean) => {
  if (subMenuIsOpen) {
    return '0.75rem';
  }

  if (isBaseLevel) {
    return '1.5rem';
  }

  return '1rem';
};
